import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { GraduationCap, CheckCircle, Copy, ExternalLink, ArrowRight } from 'lucide-react';

function SuccessPage() {
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const storedCredentials = localStorage.getItem('adminCredentials');
    if (storedCredentials) {
      setCredentials(JSON.parse(storedCredentials));
    }
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!credentials) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center py-6">
            <div className="flex items-center space-x-2">
              <GraduationCap className="w-8 h-8 text-primary" />
              <span className="text-2xl font-bold text-primary">INTUIT LMS</span>
            </div>
          </div>
        </div>
      </header>

      {/* Success Content */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Welcome to INTUIT LMS!
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Your institution account has been successfully created. You can now access your admin dashboard
              to start managing courses, students, and lecturers.
            </p>
          </div>

          {/* Credentials Card */}
          <div className="card mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Admin Credentials</h2>
            <p className="text-gray-600 mb-8">
              Save these credentials securely. You can change your password after logging in.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                <div className="flex items-center space-x-3">
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                    {credentials.email}
                  </code>
                  <button
                    onClick={() => copyToClipboard(credentials.email)}
                    className="p-2 text-gray-500 hover:text-primary transition-colors"
                    title="Copy email"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
                <div className="flex items-center space-x-3">
                  <code className="flex-1 bg-white px-3 py-2 rounded border text-sm font-mono">
                    {credentials.password}
                  </code>
                  <button
                    onClick={() => copyToClipboard(credentials.password)}
                    className="p-2 text-gray-500 hover:text-primary transition-colors"
                    title="Copy password"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {copied && (
              <div className="mt-4 text-sm text-green-600 font-medium">
                Copied to clipboard!
              </div>
            )}
          </div>

          {/* Next Steps */}
          <div className="card mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Next Steps</h2>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">1</span>
                </div>
                <h3 className="font-semibold mb-2">Login to Dashboard</h3>
                <p className="text-gray-600 text-sm">
                  Use your credentials to access the admin dashboard
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">2</span>
                </div>
                <h3 className="font-semibold mb-2">Set Up Your Institution</h3>
                <p className="text-gray-600 text-sm">
                  Add courses, units, and configure your academic structure
                </p>
              </div>

              <div className="text-center">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold">3</span>
                </div>
                <h3 className="font-semibold mb-2">Invite Users</h3>
                <p className="text-gray-600 text-sm">
                  Add lecturers and students to start using the system
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://admin.intuitlms.online"
              className="btn-primary text-lg px-8 py-4 inline-flex items-center"
            >
              Go to Admin Dashboard
              <ExternalLink className="w-5 h-5 ml-2" />
            </a>
            <Link
              to="/"
              className="btn-secondary text-lg px-8 py-4 inline-flex items-center"
            >
              Back to Home
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>

          {/* Support */}
          <div className="mt-12 text-center">
            <p className="text-gray-600">
              Need help getting started?{' '}
              <a href="mailto:support@intuitlms.online" className="text-primary hover:text-blue-700 font-medium">
                Contact our support team
              </a>
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

export default SuccessPage;
